import React from 'react';
import { Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { CircleDot, CircleOff, CalendarCheck } from 'lucide-react';
import { formatDateTime } from './helpers';

// Wide "Asset Status" card spanning the full Asset Health row.
// Left cell: title + Live pill + "Real time — Last updated …"
// Right cells: Online / Offline (Unplanned) / Offline (Planned), each with
// a big number on top and an icon + label below, separated by faint vertical
// dividers — matches the reference design exactly.

const COLORS = {
  online: '#10b981',
  unplanned: '#ef4444',
  planned: '#6b7280',
};

const Count = ({ count, label, icon: Icon, color }) => (
  <div className="d-flex flex-column">
    <div className="font-size-24 fw-bold">{count ?? 0}</div>
    <div className="d-flex align-items-center gap-1 text-muted font-size-12">
      <Icon size={14} style={{ color }} />
      <span>{label}</span>
    </div>
  </div>
);

export default function AssetStatusCard({ data }) {
  const { t } = useTranslation();
  const online = data?.onlineCount ?? 0;
  const unplanned = data?.offlineUnplannedCount ?? 0;
  const planned = data?.offlinePlannedCount ?? 0;
  const lastUpdated = data?.lastUpdated;

  // Faint vertical divider between cells — same color as card border tokens.
  const divider = { borderLeft: '1px solid #e5e7eb' };

  return (
    <Card>
      <CardBody className="d-flex flex-wrap align-items-center gap-4">
        <div className="d-flex flex-column" style={{ minWidth: 0, flex: '1 1 280px' }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="fw-semibold">
              {t('shared.global_org.overview.asset_status_card_title', 'Asset Status')}
            </span>
            <span
              className="badge"
              style={{ backgroundColor: '#10b981', color: '#fff', fontWeight: 500 }}
            >
              {t('shared.global_org.overview.live_badge', 'Live')}
            </span>
          </div>
          <div className="text-muted font-size-12">
            {t('shared.global_org.overview.last_updated_at',
              'Real time — Last updated {{value}}',
              { value: formatDateTime(lastUpdated) })}
          </div>
        </div>
        <div className="ps-4" style={divider}>
          <Count
            count={online}
            label={t('shared.online', 'Online')}
            icon={CircleDot}
            color={COLORS.online}
          />
        </div>
        <div className="ps-4" style={divider}>
          <Count
            count={unplanned}
            label={t('shared.offline_unplanned', 'Offline (Unplanned)')}
            icon={CircleOff}
            color={COLORS.unplanned}
          />
        </div>
        <div className="ps-4" style={divider}>
          <Count
            count={planned}
            label={t('shared.offline_planned', 'Offline (Planned)')}
            icon={CalendarCheck}
            color={COLORS.planned}
          />
        </div>
      </CardBody>
    </Card>
  );
}
